import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import cl from 'classnames';
import { useListPosition } from 'src/hooks';
import { FieldValue } from 'src/utils/fieldHandler';
import styles from './selectWithLabelStyle.module.scss';

type SelectWithLabelProps = {
  label: string;
  value: FieldValue;
  items: {
    value: FieldValue;
    label: string;
    inactive?: boolean;
  }[];
  selectItem: (value: FieldValue) => void;
  design?: 'default' | 'filter' | 'filterModal';
  size?: 'small' | 'middle';
  autoSize?: boolean;
  className?: string;
  disabled?: boolean | null;
  isLoading?: boolean;
  excludeCurrent?: boolean;
  expandOnTop?: boolean;
};

export const SelectWithLabel = ({
  label,
  value,
  items,
  selectItem,
  design = 'default',
  size = 'middle',
  autoSize = false,
  className,
  disabled = false,
  isLoading = false,
  excludeCurrent = false,
  expandOnTop = false,
}: SelectWithLabelProps) => {
  const { isView, toHide, btnRef, portalRef, onBtnClick, listPosition } =
    useListPosition(expandOnTop);

  const currentItem = useMemo(
    () => items.find(({ value: itemValue }) => itemValue === value),
    [items, value],
  );

  const currentLabel = useMemo(
    () => currentItem?.label ?? (value === null ? '---' : String(value)),
    [currentItem?.label, value],
  );

  const isInactive = useMemo(
    () => (currentItem ? Boolean(currentItem.inactive) : false),
    [currentItem],
  );

  const filteredItems = useMemo(
    () =>
      excludeCurrent
        ? items.filter(({ value: itemValue }) => value !== itemValue)
        : items,
    [excludeCurrent, items, value],
  );

  const isDisabled = useMemo(
    () => disabled || !currentItem,
    [disabled, currentItem],
  );

  return (
    <div
      ref={btnRef}
      className={cl(styles.container, isDisabled && styles.disabled, className)}
      onClick={(event) => {
        if (isLoading || isDisabled) {
          return;
        }

        onBtnClick(event);
      }}
    >
      <div
        className={cl(
          styles.inputContainer,
          styles[design],
          size === 'small' && styles.small,
          autoSize && styles.autoSize,
        )}
      >
        {isLoading && (
          <div className={styles.loading}>
            <div className={styles.loader} />
          </div>
        )}
        <label
          className={cl(
            styles.label,
            styles[design],
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
          )}
        >
          {label}
        </label>
        <div
          className={cl(
            styles.value,
            styles[design],
            isInactive && styles.inactive,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
          )}
        >
          {isLoading ? <>&nbsp;</> : currentLabel}
        </div>
        <svg
          width="14"
          height="16"
          viewBox="0 0 14 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cl(
            styles.arrowIcon,
            styles[design],
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
            isView === Boolean(expandOnTop) && styles.expanded,
          )}
        >
          <path
            d="M11.0827 6.05664L6.99935 10.5937L2.91602 6.05664"
            stroke="#323C46"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {listPosition &&
        createPortal(
          <div className={styles.portal} onClick={toHide} ref={portalRef}>
            <div
              className={styles.relativeContainer}
              onClick={toHide}
              style={{ ...listPosition }}
            >
              <div
                className={cl(
                  styles.absoluteContainer,
                  expandOnTop && styles.expandOnTop,
                )}
              >
                <div
                  className={cl(styles.listWrapper, isView && styles.expanded)}
                  style={{ width: listPosition.width }}
                >
                  <div
                    className={styles.list}
                    style={{ width: listPosition.width }}
                  >
                    {filteredItems.map(
                      ({ value: itemValue, label, inactive }) => (
                        <div
                          key={`select-wl-${itemValue}`}
                          className={cl(
                            styles.listItem,
                            styles[design],
                            size === 'small' && styles.small,
                            autoSize && styles.autoSize,
                            value === itemValue && styles.current,
                            inactive && styles.inactive,
                          )}
                          onClick={(event) => {
                            if (inactive) {
                              event.stopPropagation();
                              return;
                            }

                            toHide(event);
                            selectItem(itemValue);
                          }}
                        >
                          {label}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SelectWithLabel;
